import { createCustomerAuthority, type MergeCustomerCommand } from '../customer/customer-authority.ts';
import { createLoyaltyLedger, type ManualAdjustmentCommand } from '../loyalty/loyalty-ledger.ts';
import {
  createBenefitAuthority,
  type BenefitMutationContext,
  type IssuedCouponSnapshot,
  type PublishBenefitDefinitionCommand,
  type PublishMilestoneRuleCommand,
  type ValidatedPricingBenefit,
} from '../loyalty/benefit-authority.ts';
import type { OperationalFactInput } from '../facts/operational-facts.ts';

export interface C4OrderEvidence {
  readonly orderId: string;
  readonly revision: number;
  readonly customerId?: string;
  readonly currency: string;
  readonly totalMinor: number;
  readonly businessDate: string;
}

export interface C4PaymentEvidence {
  readonly orderId: string;
  readonly orderRevision: number;
  readonly currency: string;
  readonly totalMinor: number;
  readonly fullySettled: boolean;
}

export interface C4PaymentEventEvidence {
  readonly eventId: string;
  readonly type: string;
  readonly amountMinor: number;
  readonly at: string;
}

export interface C4FulfillmentEvidence {
  readonly orderId: string;
  readonly orderRevision: number;
  readonly state: string;
}

export type C4StaffPermission = 'customer.manage' | 'loyalty.adjust';

export interface C4CustomerLoyaltyDependencies {
  readonly getOrder: (orderId: string) => C4OrderEvidence | null;
  readonly getPayment: (orderId: string) => C4PaymentEvidence;
  readonly getPaymentEvents: (orderId: string) => readonly C4PaymentEventEvidence[];
  readonly getFulfillment: (orderId: string) => C4FulfillmentEvidence;
  readonly authorizeStaff: (input: { readonly actorId: string; readonly permission: C4StaffPermission; readonly at: string }) => void;
  readonly appendFact: (fact: OperationalFactInput) => unknown;
}

export interface C4MergeCustomersCommand extends MergeCustomerCommand {
  readonly actorId: string;
  readonly idempotencyKey: string;
  readonly at: string;
}

export interface C4CouponSelection {
  readonly couponId: string;
  readonly expectedVersion: number;
}

export interface C4ReserveCouponsCommand {
  readonly customerId: string;
  readonly selections: readonly C4CouponSelection[];
  readonly checkoutRef: string;
  readonly candidateSignature: string;
  readonly orderTotalMinor: number;
  readonly productIds: readonly string[];
  readonly at: string;
}

export interface C4ReservedCoupon {
  readonly couponId: string;
  readonly version: number;
  readonly checkoutRef: string;
}

export interface C4ReservedCouponsResult {
  readonly reservations: readonly C4ReservedCoupon[];
  readonly validatedBenefits: readonly ValidatedPricingBenefit[];
}

export interface C4ReconcileResult {
  readonly changed: boolean;
  readonly customerId: string | null;
  readonly accrued: boolean;
  readonly reversedRefundEvidenceIds: readonly string[];
  readonly issuedCouponIds: readonly string[];
}

const RESERVATION_LEASE_MS = 60_000;

const businessDateFromTimestamp = (at: string): string => {
  const match = /^(\d{4}-\d{2}-\d{2})T/.exec(at);
  if (!match) throw new Error('C4_TIMESTAMP_INVALID');
  return match[1]!;
};

const timestampMs = (at: string): number => {
  const parsed = Date.parse(at);
  if (!Number.isFinite(parsed)) throw new Error('C4_TIMESTAMP_INVALID');
  return parsed;
};

const assertText = (value: string, code: string): string => {
  const accepted = value?.trim();
  if (!accepted) throw new Error(code);
  return accepted;
};

const assertCustomer = (customer: ReturnType<ReturnType<typeof createCustomerAuthority>['get']>): NonNullable<typeof customer> => {
  if (!customer || customer.status !== 'active') throw new Error('C4_CUSTOMER_NOT_ACTIVE');
  return customer;
};

export function createC4CustomerLoyaltyControlPlane(deps: C4CustomerLoyaltyDependencies) {
  const customer = createCustomerAuthority();
  const loyalty = createLoyaltyLedger();
  const benefit = createBenefitAuthority();
  const orderCustomerLinks = new Map<string, string>();

  const appendCustomerFact = (
    eventId: string,
    customerId: string,
    factType: string,
    at: string,
    payload: unknown,
    revision: string,
    businessDate = businessDateFromTimestamp(at),
  ): void => {
    deps.appendFact({
      eventId,
      aggregateType: 'customer',
      aggregateId: customerId,
      factType,
      businessDate,
      occurredAt: at,
      recordedAt: at,
      schemaVersion: 1,
      source: { authority: 'c4-customer-loyalty', revision },
      payload,
    });
  };

  const canonicalLinkedCustomer = (order: C4OrderEvidence): string | null => {
    const reference = orderCustomerLinks.get(order.orderId) ?? order.customerId;
    if (!reference) return null;
    const canonicalCustomerId = customer.canonicalCustomerId(reference);
    assertCustomer(customer.get(canonicalCustomerId));
    return canonicalCustomerId;
  };

  const hasAccrual = (customerId: string, orderId: string): boolean =>
    loyalty.events(customerId).some((event) => event.orderId === orderId && event.type === 'growth_accrued');

  const hasRefundReversal = (customerId: string, refundEvidenceId: string): boolean =>
    loyalty.events(customerId).some((event) =>
      event.referenceId === refundEvidenceId && event.type === 'growth_reversed');

  const issueMilestones = (
    customerId: string,
    at: string,
    idempotencyKey: string,
    businessDate: string,
  ): readonly string[] => {
    const balance = loyalty.balance(customerId);
    const issued = benefit.issueEligibleMilestones({
      customerId,
      seedBalance: balance.memorySeed,
      idempotencyKey,
      at,
    });
    for (const coupon of issued) {
      appendCustomerFact(
        `c4:${coupon.couponId}:issued`,
        customerId,
        'benefit.coupon.issued',
        coupon.issuedAt,
        { couponId: coupon.couponId, definitionId: coupon.definitionId, milestoneRuleId: coupon.milestoneRuleId, expiresAt: coupon.expiresAt },
        `${coupon.couponId}:v${coupon.version}`,
        businessDate,
      );
    }
    return issued.map((coupon) => coupon.couponId);
  };

  const reconcileOrder = (orderIdInput: string, at: string): C4ReconcileResult => {
    const orderId = assertText(orderIdInput, 'C4_ORDER_ID_REQUIRED');
    timestampMs(at);
    const order = deps.getOrder(orderId);
    if (!order) throw new Error('C4_ORDER_NOT_FOUND');
    const canonicalCustomerId = canonicalLinkedCustomer(order);
    if (!canonicalCustomerId) {
      return { changed: false, customerId: null, accrued: false, reversedRefundEvidenceIds: [], issuedCouponIds: [] };
    }
    const payment = deps.getPayment(orderId);
    const fulfillment = deps.getFulfillment(orderId);
    if (payment.orderId !== order.orderId || payment.orderRevision !== order.revision ||
        fulfillment.orderId !== order.orderId || fulfillment.orderRevision !== order.revision) {
      throw new Error('C4_ORDER_EVIDENCE_MISMATCH');
    }
    if (payment.currency !== order.currency || payment.totalMinor !== order.totalMinor) {
      throw new Error('C4_PAYMENT_EVIDENCE_MISMATCH');
    }
    if (order.currency !== 'HKD') throw new Error('C4_LOYALTY_CURRENCY_UNSUPPORTED');

    let changed = false;
    let accrued = false;
    const reversedRefundEvidenceIds: string[] = [];

    if (payment.fullySettled && fulfillment.state === 'completed' && !hasAccrual(canonicalCustomerId, order.orderId)) {
      const result = loyalty.accrueSale({
        customerId: canonicalCustomerId,
        orderId: order.orderId,
        orderRevision: order.revision,
        currency: 'HKD',
        eligibleMinor: order.totalMinor,
        idempotencyKey: `c4:accrual:${order.orderId}:r${order.revision}`,
        at,
      });
      changed = true;
      accrued = true;
      if (result.growthQuantity > 0 || result.memorySeedQuantity > 0) {
        appendCustomerFact(
          `c4:${order.orderId}:accrued:r${order.revision}`,
          canonicalCustomerId,
          'loyalty.accrued',
          at,
          { orderId: order.orderId, eligibleMinor: result.eligibleMinor, growth: result.growthQuantity, memorySeed: result.memorySeedQuantity },
          `${order.orderId}:r${order.revision}`,
          order.businessDate,
        );
      }
    }

    if (hasAccrual(canonicalCustomerId, order.orderId)) {
      for (const event of deps.getPaymentEvents(orderId)) {
        if (event.type !== 'refund' || hasRefundReversal(canonicalCustomerId, event.eventId)) continue;
        const reversed = loyalty.reverseRefund({
          customerId: canonicalCustomerId,
          orderId: order.orderId,
          orderRevision: order.revision,
          refundEvidenceId: event.eventId,
          refundMinor: event.amountMinor,
          idempotencyKey: `c4:refund:${event.eventId}`,
          at: event.at,
        });
        if (reversed.reversedGrowthQuantity > 0 || reversed.reversedMemorySeedQuantity > 0) {
          changed = true;
          reversedRefundEvidenceIds.push(event.eventId);
          appendCustomerFact(
            `c4:${event.eventId}:reversed`,
            canonicalCustomerId,
            'loyalty.reversed',
            event.at,
            { orderId: order.orderId, refundEvidenceId: event.eventId, refundMinor: event.amountMinor, growth: reversed.reversedGrowthQuantity, memorySeed: reversed.reversedMemorySeedQuantity },
            event.eventId,
            order.businessDate,
          );
        }
      }
    }

    let issuedCouponIds: readonly string[] = [];
    if (changed) {
      const latestLoyaltyEvent = loyalty.events(canonicalCustomerId).at(-1);
      issuedCouponIds = issueMilestones(
        canonicalCustomerId,
        latestLoyaltyEvent?.at ?? at,
        `c4:milestones:${latestLoyaltyEvent?.eventId ?? `${order.orderId}:none`}`,
        order.businessDate,
      );
    }

    return {
      changed,
      customerId: canonicalCustomerId,
      accrued,
      reversedRefundEvidenceIds,
      issuedCouponIds: [...issuedCouponIds],
    };
  };

  return {
    customer,
    loyalty,
    benefit,

    linkOrderCustomer(input: { readonly orderId: string; readonly customerId: string }): string {
      const orderId = assertText(input.orderId, 'C4_ORDER_ID_REQUIRED');
      const canonicalCustomerId = customer.canonicalCustomerId(assertText(input.customerId, 'C4_CUSTOMER_ID_REQUIRED'));
      assertCustomer(customer.get(canonicalCustomerId));
      const current = orderCustomerLinks.get(orderId);
      if (current && customer.canonicalCustomerId(current) !== canonicalCustomerId) throw new Error('C4_ORDER_CUSTOMER_CONFLICT');
      orderCustomerLinks.set(orderId, canonicalCustomerId);
      return canonicalCustomerId;
    },

    customerForOrder(orderId: string): string | null {
      const order = deps.getOrder(assertText(orderId, 'C4_ORDER_ID_REQUIRED'));
      if (!order) return null;
      return canonicalLinkedCustomer(order);
    },

    reconcileOrder,

    manualAdjust(command: ManualAdjustmentCommand) {
      deps.authorizeStaff({ actorId: command.actorId, permission: 'loyalty.adjust', at: command.at });
      const canonicalCustomerId = customer.canonicalCustomerId(command.customerId);
      assertCustomer(customer.get(canonicalCustomerId));
      const result = loyalty.manualAdjust({ ...command, customerId: canonicalCustomerId });
      appendCustomerFact(
        `c4:manual:${command.idempotencyKey}`,
        canonicalCustomerId,
        'loyalty.manually_adjusted',
        command.at,
        { asset: command.asset, quantity: command.quantity, actorId: command.actorId, reason: command.reason },
        command.idempotencyKey,
      );
      const issuedCouponIds = issueMilestones(
        canonicalCustomerId,
        command.at,
        `c4:milestones:manual:${command.idempotencyKey}`,
        businessDateFromTimestamp(command.at),
      );
      return { ...result, issuedCouponIds };
    },

    mergeCustomers(command: C4MergeCustomersCommand) {
      deps.authorizeStaff({ actorId: command.actorId, permission: 'customer.manage', at: command.at });
      const preview = customer.prepareMerge({
        sourceCustomerId: command.sourceCustomerId,
        targetCustomerId: command.targetCustomerId,
        expectedSourceVersion: command.expectedSourceVersion,
        expectedTargetVersion: command.expectedTargetVersion,
      });
      const transfer = loyalty.transferCustomerAssets({
        sourceCustomerId: preview.source.customerId,
        targetCustomerId: preview.target.customerId,
        actorId: command.actorId,
        reason: command.reason,
        idempotencyKey: `${command.idempotencyKey}:assets`,
        at: command.at,
      });
      const merged = customer.merge(
        { actorId: command.actorId, idempotencyKey: `${command.idempotencyKey}:customer`, at: command.at },
        {
          sourceCustomerId: command.sourceCustomerId,
          targetCustomerId: command.targetCustomerId,
          expectedSourceVersion: command.expectedSourceVersion,
          expectedTargetVersion: command.expectedTargetVersion,
          reason: command.reason,
        },
      );
      appendCustomerFact(
        `c4:merge:${command.idempotencyKey}`,
        merged.target.customerId,
        'customer.merged',
        command.at,
        { sourceCustomerId: merged.source.customerId, targetCustomerId: merged.target.customerId, transferredEvents: transfer.events.map((event) => event.eventId), reason: command.reason },
        command.idempotencyKey,
      );
      const issuedCouponIds = issueMilestones(
        merged.target.customerId,
        command.at,
        `c4:milestones:merge:${command.idempotencyKey}`,
        businessDateFromTimestamp(command.at),
      );
      return { customer: merged, loyalty: transfer, issuedCouponIds };
    },

    publishBenefitDefinition(context: BenefitMutationContext, command: PublishBenefitDefinitionCommand) {
      deps.authorizeStaff({ actorId: context.actorId, permission: 'customer.manage', at: context.at });
      return benefit.publishDefinition(context, command);
    },

    publishMilestoneRule(context: BenefitMutationContext, command: PublishMilestoneRuleCommand) {
      deps.authorizeStaff({ actorId: context.actorId, permission: 'customer.manage', at: context.at });
      return benefit.publishMilestoneRule(context, command);
    },

    reserveCoupons(command: C4ReserveCouponsCommand): C4ReservedCouponsResult {
      const customerId = customer.canonicalCustomerId(assertText(command.customerId, 'C4_CUSTOMER_ID_REQUIRED'));
      assertCustomer(customer.get(customerId));
      const checkoutRef = assertText(command.checkoutRef, 'C4_CHECKOUT_REF_REQUIRED');
      const candidateSignature = assertText(command.candidateSignature, 'C4_CANDIDATE_SIGNATURE_REQUIRED');
      const now = timestampMs(command.at);
      const reservations: C4ReservedCoupon[] = [];
      const validatedBenefits: ValidatedPricingBenefit[] = [];

      for (const selection of command.selections) {
        const coupon = benefit.coupon(selection.couponId);
        if (!coupon) throw new Error('BENEFIT_COUPON_NOT_FOUND');
        if (coupon.customerId !== customerId) throw new Error('BENEFIT_COUPON_CUSTOMER_MISMATCH');
        if (coupon.version !== selection.expectedVersion) throw new Error('BENEFIT_COUPON_VERSION_CONFLICT');
      }

      try {
        for (const selection of command.selections) {
          const coupon = benefit.coupon(selection.couponId)!;
          const leaseUntil = Math.min(now + RESERVATION_LEASE_MS, timestampMs(coupon.expiresAt));
          if (leaseUntil <= now) throw new Error('BENEFIT_COUPON_EXPIRED');
          const result = benefit.reserve({
            actorId: `customer:${customerId}`,
            idempotencyKey: `c4:reserve:${checkoutRef}:${selection.couponId}:v${selection.expectedVersion}`,
            at: command.at,
          }, {
            couponId: selection.couponId,
            customerId,
            expectedVersion: selection.expectedVersion,
            checkoutRef,
            candidateSignature,
            orderTotalMinor: command.orderTotalMinor,
            productIds: command.productIds,
            reservationExpiresAt: new Date(leaseUntil).toISOString(),
          });
          reservations.push({ couponId: result.coupon.couponId, version: result.coupon.version, checkoutRef });
          validatedBenefits.push(result.validatedBenefit);
          appendCustomerFact(
            `c4:${result.coupon.couponId}:reserved:v${result.coupon.version}`,
            customerId,
            'benefit.coupon.reserved',
            command.at,
            { couponId: result.coupon.couponId, checkoutRef, candidateSignature },
            `${result.coupon.couponId}:v${result.coupon.version}`,
          );
        }
      } catch (error) {
        for (const reserved of reservations) {
          const current = benefit.coupon(reserved.couponId);
          if (!current || current.status !== 'reserved' || current.reservation?.checkoutRef !== checkoutRef) continue;
          benefit.releaseReservation({
            actorId: `customer:${customerId}`,
            idempotencyKey: `c4:rollback:${checkoutRef}:${reserved.couponId}:v${current.version}`,
            at: command.at,
          }, { couponId: reserved.couponId, expectedVersion: current.version, checkoutRef });
        }
        throw error;
      }

      return { reservations, validatedBenefits };
    },

    releaseCoupons(checkoutRefInput: string, reservations: readonly C4ReservedCoupon[], at: string): readonly IssuedCouponSnapshot[] {
      const checkoutRef = assertText(checkoutRefInput, 'C4_CHECKOUT_REF_REQUIRED');
      return reservations.map((reservation) => benefit.releaseReservation({
        actorId: 'c4-runtime',
        idempotencyKey: `c4:release:${checkoutRef}:${reservation.couponId}:v${reservation.version}`,
        at,
      }, {
        couponId: reservation.couponId,
        expectedVersion: reservation.version,
        checkoutRef,
      }));
    },

    commitCoupons(orderIdInput: string, checkoutRefInput: string, reservations: readonly C4ReservedCoupon[], at: string): readonly IssuedCouponSnapshot[] {
      const orderId = assertText(orderIdInput, 'C4_ORDER_ID_REQUIRED');
      const checkoutRef = assertText(checkoutRefInput, 'C4_CHECKOUT_REF_REQUIRED');
      const order = deps.getOrder(orderId);
      if (!order) throw new Error('C4_ORDER_NOT_FOUND');

      for (const reservation of reservations) {
        const current = benefit.coupon(reservation.couponId);
        if (!current || current.status !== 'reserved' || current.version !== reservation.version || current.reservation?.checkoutRef !== checkoutRef) {
          throw new Error('C4_COUPON_RESERVATION_STALE');
        }
      }

      return reservations.map((reservation) => {
        const redeemed = benefit.commitRedemption({
          actorId: 'c4-runtime',
          idempotencyKey: `c4:redeem:${orderId}:${reservation.couponId}:v${reservation.version}`,
          at,
        }, {
          couponId: reservation.couponId,
          expectedVersion: reservation.version,
          checkoutRef,
          orderId,
        });
        appendCustomerFact(
          `c4:${redeemed.couponId}:redeemed:${orderId}`,
          redeemed.customerId,
          'benefit.coupon.redeemed',
          at,
          { couponId: redeemed.couponId, orderId },
          `${redeemed.couponId}:v${redeemed.version}`,
          order.businessDate,
        );
        return redeemed;
      });
    },

    canonicalCustomerId(customerId: string): string {
      return customer.canonicalCustomerId(customerId);
    },

    customerCoupons(customerId: string) {
      return benefit.customerCoupons(customer.canonicalCustomerId(customerId));
    },
  };
}
